import React from 'react';
import { MenuItem, Select, ListItemText, Checkbox, Chip, FormControl, SelectChangeEvent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import HighlightText from '@/components/HighlightText/HighlightText';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import { chipTypes, fieldTypes } from '@/helpers/constants';

const useStyles = makeStyles(() => ({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
  chipsDisabled: {
    marginLeft: '10px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
}));

type ChipsMultiSelectorProps = {
  options: string[];
  formControlClass: string;
  value: string[];
  onSelectionChange: (event: SelectChangeEvent<string[]>) => void; // eslint-disable-line no-unused-vars
  onDelete: (value: string) => void; // eslint-disable-line no-unused-vars
  size?: 'small' | 'medium';
  checkEnabled: boolean;
  fieldPath: string[];
  id: string;
};

const ChipsMultiSelector: React.FC<ChipsMultiSelectorProps> = (props) => {
  const { options = [], formControlClass, value, onSelectionChange = () => {}, onDelete = () => {}, size = 'small',
    checkEnabled, fieldPath, id } = props;
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const translate = (item: string): string => {
    const key = `affectedVehicles.${item}`;

    if (i18n.exists(key)) {
      return `${item} - ${t(key)}`;
    }

    return item;
  };

  return (
    <FormControl className={formControlClass}>
      <Select
        disabled={checkEnabled}
        labelId="chip-multi-selector-label"
        id="chip-multi-selector"
        variant="standard"
        value={value}
        multiple
        onChange={onSelectionChange}
        renderValue={(selected: string[]) => (
          <div className={classes.chips}>
            {checkEnabled
              ? (
                <UniversalChip
                  id={id}
                  chipType={chipTypes.READONLY}
                  arrayStrings={selected.map((item) => translate(item))}
                  classNameContainer={classes.chipsDisabled}
                  fieldPath={fieldPath}
                />
              )
              : (selected.map((item, index) => (
                <Chip
                  id={id}
                  disabled={checkEnabled}
                  clickable
                  key={item}
                  label={(
                    <HighlightText
                      fieldId={id}
                      fieldPath={[fieldPath[0], fieldPath[1], `${fieldPath[2]}[${index}]`]}
                      fieldType={fieldTypes.CHIP}
                    >{translate(item)}
                    </HighlightText>
                  )}
                  size={size}
                  onDelete={() => onDelete(item)}
                  className={classes.chip}
                  onMouseDown={(event) => {
                    onDelete(item);
                    event.stopPropagation();
                  }}
                />
              )))}
          </div>
        )}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              color="primary"
              checked={value.indexOf(option) > -1}
            />
            <ListItemText primary={translate(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ChipsMultiSelector;
